import { REQUEST_STATUS } from "@/ts/enums/RequestStatus";
import { getBaseRoute } from "./route";

const paramGradeIds: Record<number, string> = {
  120: "g12",
  110: "g11",
  100: "g10",
  90: "g9",
  80: "g8",
  70: "g7",
  60: "g6",
  50: "g5",
  40: "g4",
  30: "g3",
  20: "g2",
  10: "g1",
  1: "f",
  901: "p" // preparatory
};

export const mapGradeCodeToGradeParamId = (
  gradeCode: number
): string | undefined => paramGradeIds[gradeCode];

// ---- reverse map ----
const gradeCodeByParamId: Record<string, number> = Object.fromEntries(
  Object.entries(paramGradeIds).map(([code, param]) => [param, Number(code)])
);

export const mapGradeParamIdToGradeCode = (
  paramId: string
): number | undefined => gradeCodeByParamId[paramId];

export const stripPrefix = (value: string, prefix: string) =>
  value.startsWith(prefix) ? value.slice(prefix.length) : value;

/**
 * Convert a subject name into a URL-safe slug.
 * Used to build the subject segment of assignment detail URLs.
 *
 * Examples:
 *   "English"                        → "english"
 *   "Health and Physical Education"  → "health-and-physical-education"
 *   "Science & Technology"           → "science-technology"
 */
export const slugifySubject = (subject: string): string =>
  subject
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-") // Any run of non-alphanumerics → single hyphen
    .replace(/^-|-$/g, "");      // Trim leading/trailing hyphen

export const normalizeBase = (base: string) => {
  let s = base || "/";

  // Trim trailing slashes down to at most 1 (but never trim the single root "/")
  let end = s.length;
  while (end > 1 && s.charCodeAt(end - 1) === 47 /* '/' */) {
    end--;
  }
  s = s.slice(0, end);

  // Ensure exactly one trailing slash
  return s.endsWith("/") ? s : s + "/";
};

export const replaceGradeInPath = (pathname: string, nextGrade: string) => {
  const b = normalizeBase(getBaseRoute());
  if (!pathname.startsWith(b)) return `${b}${nextGrade}`;

  const rest = pathname.slice(b.length); // after "/learning/"
  const parts = rest.split("/"); // ["g10","terms","..."]

  if (!parts[0]) return `${b}${nextGrade}`; // landing at base
  parts[0] = nextGrade;

  return b + parts.join("/");
};

export const ensureGradeInPath = (pathname: string, grade: string) => {
  const fallbackSubpath = "not-found";
  const b = normalizeBase(getBaseRoute());

  if (!pathname.startsWith(b))
    return `${b}/${fallbackSubpath}`.replace(/\/$/, "");

  const rest = pathname.slice(b.length);
  const parts = rest.split("/");

  if (!parts[0]) return `${b}/${fallbackSubpath}`.replace(/\/$/, "");
  return replaceGradeInPath(pathname, grade);
};

export const extractTermIdFromLessonSlug = (slug: string): string | null => {
  const m = slug.match(/(?:^|-)term-(\d+)(?:-|$)/i);
  return m ? m[1] : null;
};

/**
 * Grade 12 is a 3-term academic year — Term 4 does not exist.
 * Single source of truth for that cap across all content types (lessons and assignments).
 */
const GRADE_12_CODE = 120;
const GRADE_12_MAX_TERM_ID = "3";

/**
 * Returns true if the given term ID is valid for the grade.
 *
 * Grade 12 is capped at Term 3 — Term 4 and above do not exist.
 * All other grades accept any term returned by the API.
 *
 * @param gradeCode - The numeric grade code (e.g. 120 for Grade 12)
 * @param termId    - The numeric term ID string (e.g. "4")
 */
export function isTermValidForGrade(gradeCode: number, termId: string): boolean {
  if (gradeCode === GRADE_12_CODE && termId > GRADE_12_MAX_TERM_ID) return false;
  return true;
}

/**
 * Returns true when the URL term is a valid, navigable route for this grade.
 *
 * Consolidates two checks used by any container with a term URL param:
 *  1. Grade-specific term cap (Grade 12 max Term 3)
 *  2. Term present in the loaded terms list
 *
 * Returns true when `termId` or `gradeCode` is absent — those cases are
 * handled elsewhere (loading state, grade guard).
 *
 * @param termId      - Numeric term ID from the URL, prefix already stripped (e.g. "5")
 * @param gradeCode   - The student's grade code
 * @param termsStatus - The terms fetch status from context
 * @param selectedTerm - The resolved term object, or null when not found
 */
export function isValidTermRoute(
  termId: string | undefined,
  gradeCode: number | null | undefined,
  termsStatus: string | undefined,
  selectedTerm: { id: string } | null | undefined
): boolean {
  if (!termId || !gradeCode) return true;
  if (!isTermValidForGrade(gradeCode, termId)) return false;
  if (termsStatus === REQUEST_STATUS.SUCCESS && !selectedTerm) return false;
  return true;
}
